import { invariant } from '../util/invariant';
import { Guard } from './async';

/*
  Internal class used implement AsyncIterableBuffer. Implements both consuming
  and producing an aaync iterable object. See
  https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/for-await...of
*/
class BufferedIterator<T> {
  buffered: T[] = [];
  end = false;
  stopping = false;
  consuming = false;
  exception: any;
  enqueueGuard: Guard = new Guard();
  dequeueGuard: Guard = new Guard();
  sizeLimit: number;

  constructor(bufferLimit: number, private readonly producer: AsyncIterable<T>) {
    if (bufferLimit < 1) {
      throw Error('Cannot buffer less than one value');
    }

    /*
      One value is kept in the context waiting for guards and is never put into
      the buffer array.
    */
    this.sizeLimit = bufferLimit - 1;
  }

  async produce() {
    try {
      for await (const v of this.producer) {
        if (this.stopping) {
          break;
        }

        if (this.buffered.length >= this.sizeLimit) {
          await this.enqueueGuard.wait();
        }

        if (this.stopping) {
          break;
        }

        invariant(this.buffered.length < this.sizeLimit, 'Expected free space in buffer');
        this.buffered.push(v);
        this.dequeueGuard.notify();
      }
    } catch (ex) {
      this.exception = ex;
    }

    this.end = true;
    this.dequeueGuard.notify();
  }

  async stop() {
    if (this.end)
      return;

    this.stopping = true;
    this.enqueueGuard.notify();
    while (!this.end) {
      await this.dequeueGuard.wait();
    }
  }

  async* consume(): AsyncGenerator<T> {
    try {
      if (this.consuming) {
        throw Error('Multiple consumers not supported');
      }

      this.consuming = true;

      // Loop until both end is reached and buffer is drained.
      while (!this.end || this.buffered.length) {
        if (this.exception) {
          throw this.exception;
        }

        if (this.buffered.length == 0) {
          await this.dequeueGuard.wait();
          continue;
        }

        invariant(this.buffered.length != 0, 'Expected non-empty buffer');
        const value = this.buffered.shift();
        this.enqueueGuard.notify();
        invariant(value != null, 'Value expected');

        yield value;
      }
    } finally {
      await this.stop();
    }
  }
}

/**
 * A buffer that allows an asynchronous generator to run loosely synchronized
 * with the consumer rather than running in lockstep.
 *
 * Async iterables as described in
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/for-await...of
 * allow you to decouple code that generates a sequence of objects from code
 * that consumes the same sequence of objects.
 *
 * An AsyncIterableBuffer can wrap the generating code to introduce buffering
 * between the generator and the consuming for-await loop. This allows for more
 * parallelism, since the generator and the consumer code spend less time
 * waiting for eachother.
 *
 * The generator only pauses when the buffer is full, and the consumer only
 * pauses when the buffer is empty.
 *
 * Like with plain asynchronous generators, errors that occur in the generator
 * gets propagated to the consumer. Likewise, if an error occurs in the
 * for-await loop, the generator is shut down in a manner which triggers
 * finally-clauses.
 */
export class AsyncIterableBuffer<T> implements AsyncIterable<T> {
  prefilled: BufferedIterator<T> | null = null;

  constructor(readonly bufferLimit: number, private readonly producer: AsyncIterable<T>) {
  }

  /**
   * Initiate asynchronous prefilling the buffer with output from the producer
   * before the consumer starts consuming. Returns immediately.
   *
   * If you call startPrefilling() you must either use this iterable in an "for
   * await" loop, or call close() in order to make sure the producer is properly
   * cleaned up.
   */
  public startPrefilling() {
    if (this.prefilled != null)
      return;

    this.prefilled = new BufferedIterator<T>(this.bufferLimit, this.producer);

    /*
      Since exceptions are passed to consumer, we ignore the errors from the
      producer here.
    */
    this.prefilled.produce().catch(() => {});
  }

  /**
   * Start prefilling the buffer with output from the producer before the
   * consumer starts consuming. Resolves when either at least one value is in
   * the buffer or the producer finished producing without yielding any values.
   *
   * If you call prefill() you must either use this iterable in an "for await"
   * loop, or call close() in order to make sure the producer is properly
   * cleaned up.
   */
  prefill() {
    this.startPrefilling();
    invariant(this.prefilled, 'prefilling should have started');
    return this.prefilled.dequeueGuard.wait();
  }

  /**
   * Stop and clean up any producer that is prefilling the buffer. You only need
   * to call this if you have either called startPrefilling() or prefill(), and
   * intend to abandon this AsyncIterableBuffer rather than using it in a "for
   * await" loop. However calling close() at other times is harmless, so you may
   * use it unconditionally in finally-clauses.
   */
  async close() {
    const iterator = this.prefilled;
    this.prefilled = null;
    await iterator?.stop();
  }

  [Symbol.asyncIterator](): AsyncIterator<T, any, undefined> {
    this.startPrefilling();
    invariant(this.prefilled, 'buffering should have started');
    const iterator = this.prefilled;
    this.prefilled = null;
    return iterator.consume();
  }
}
